/**
 * Numbering follows the boxes on the ICS 208-CG form, as printed by
 * `services/coms/src/routes/Forms/ICS_CG/forms/ICS208CG.ts`.
 *
 * Boxes 1 and 2 are the read-only header, with the operational period printed
 * alongside them and unnumbered on the sheet. Boxes 3 to 9 are the safety
 * message's own fields; box 10 is the signature.
 *
 * The NIMS 208 the docx descriptor is read off is a one-box Safety Message with
 * a Prepared by, so the Coast Guard numbering lives here rather than being read
 * off the descriptor.
 */
export const ICS208_BOX_LABELS = {
  incidentName: '1. Incident Name:',
  incidentLocation: '2. Incident Location:',
  operationalPeriod: 'Operational Period (Date/Time):',
  datetimeInitiated: '3. Date / Time Initiated:',
  safetyOfficer: '4. Safety Officer:',
  knownHazards: '5. Known Hazards:',
  safetyMessage: '6. Safety Message:',
  sopAndSafeWorkPractices:
    '7. Standard Operating Procedures (SOP) and Safe Work Practices:',
  requiredPpe: '8. Required Personal Protective Equipment (PPE):',
  healthAndSafetyPlan: '9. Health and Safety Plan:',
  preparedBy: '10. Prepared by:',
} as const;

/** The three hazard columns box 5 prints, in printed order. */
export const ICS208_HAZARD_GROUPS = [
  { key: 'physicalHazards', label: 'Physical Hazards' },
  { key: 'environmentalHazards', label: 'Environmental Hazards' },
  { key: 'otherHazards', label: 'Other Hazards' },
] as const;

export const ICS208_INCIDENT_NAME_HINT =
  'This box is read-only. It is filled from the incident name.';

/** Mirrors `resolveOperationalPeriodValues`, which the PDF export uses. */
export const ICS208_INCIDENT_LOCATION_HINT =
  'This box is read-only. It is filled from the incident location, falling back to the address set when the incident was created.';

export const ICS208_OPERATIONAL_PERIOD_HINT =
  'Click this box to set the scheduled start and end of the next operational period for this incident workspace. Working-period forms print that window.';

/**
 * The safety message is dated rather than period-scoped: the card shows the one
 * whose Date/Time Initiated falls inside the period being edited.
 */
export const ICS208_DATETIME_INITIATED_HINT =
  'Which operational period this safety message belongs to is decided by this timestamp, so moving it moves the message to another period.';

export const ICS208_SAFETY_OFFICER_HINT =
  'The Safety Officer named on the printed form, with the duty phone and email responders should use to reach them.';

/**
 * Hazards not on the printed sheet are written into the custom blank the form
 * reserves for them, which is why free text is kept alongside the checkboxes.
 */
export const ICS208_KNOWN_HAZARDS_HINT =
  'Tick the hazards the printed form lists. A hazard typed in the custom blank prints in the form\u2019s "Other" line rather than as a checkbox.';

export const ICS208_REQUIRED_PPE_HINT =
  'Tick the equipment the printed form lists. Everything ticked also prints as a comma-separated line above the boxes.';

export const ICS208_HEALTH_AND_SAFETY_PLAN_HINT =
  'Whether a separate site Health and Safety Plan exists, and where responders can read it. This also fills the site safety plan boxes of the 202.';
